import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, CheckCircle, AlertCircle, Loader2, Smartphone, BookOpen, Gift } from 'lucide-react';

const IseeWordsRegistration: React.FC = () => {
  const [formData, setFormData] = useState({
    grade: '',
    level: 'basic',
    email: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('/api/iseewords/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Handle non-JSON responses (like 404/405/500 from HTML error pages)
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.indexOf('application/json') !== -1) {
        data = await response.json();
      } else {
        // If response is not JSON (e.g. text/html error page), throw meaningful error
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      if (response.ok && data.success) {
        setStatus('success');
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for registering. Please wait for further notification regarding the testing.
              <br /><br />
              If you have any questions, please contact us at <a href="mailto:apps@woodlogos.net" className="text-blue-600 hover:text-blue-500">apps@woodlogos.net</a>.
            </p>
            <a href="/" className="text-blue-600 hover:text-blue-500 font-medium">
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <link rel="canonical" href="https://www.woodlogos.net/isee/registering" />
      </Helmet>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            ISEE/SSAT Words App
          </h1>
          <p className="text-xl text-gray-600 font-medium">
            Join our Closed Testing Program
          </p>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 bg-blue-50 border-b border-blue-100">
            <h3 className="text-lg leading-6 font-medium text-blue-900 flex items-center">
              <Smartphone className="h-5 w-5 mr-2" />
              Android Users Recruitment
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="prose text-gray-600 space-y-4">
              <p>
                We are inviting Android users to participate in the Google Play closed testing for our ISEE/SSAT vocabulary app.
              </p>
              
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-500" /> Content
                  </h4>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>2000+ Core Words</li>
                    <li>4000+ Practice Questions</li>
                    <li>Scientific Memory Curve</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-blue-500" /> Requirements
                  </h4>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Android Phone</li>
                    <li>Trial for 2-4 weeks (Account transferable to iPhone later)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    <Gift className="h-5 w-5 text-blue-500" /> Benefit
                  </h4>
                  <p className="pl-5 mt-1">
                    Free 1-Year Premium Membership upon successful participation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Register for Testing
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="grade" className="block text-sm font-medium text-gray-700">
                  Child's Grade (孩子年级)
                </label>
                <div className="mt-1">
                  <select
                    id="grade"
                    name="grade"
                    required
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                    value={formData.grade}
                    onChange={handleChange}
                  >
                    <option value="">Select Grade</option>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((g) => (
                      <option key={g} value={g}>Grade {g}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="level" className="block text-sm font-medium text-gray-700">
                  Target Level (打算学习的level)
                </label>
                <div className="mt-1">
                  <select
                    id="level"
                    name="level"
                    required
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                    value={formData.level}
                    onChange={handleChange}
                  >
                    <option value="Basic">Basic (2500 words)</option>
                    <option value="Lower">Lower</option>
                    <option value="Middle">Middle</option>
                    <option value="Upper">Upper</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Gmail Address (请务必填写您的 Google Play 账号绑定的邮箱)
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border"
                    placeholder="example@gmail.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <p className="mt-2 text-sm text-red-500 font-medium">
                  * Must use the Gmail address linked to your Google Play Store account, otherwise you cannot join the testing program.
                </p>
              </div>

              {status === 'error' && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        {errorMessage}
                      </h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Registration'
                  )}
                </button>
              </div>
              
              <p className="text-xs text-center text-gray-500 mt-4">
                By submitting this form, you agree to our{' '}
                <a href="/app/iseewords/privacy-policy/" className="text-blue-600 hover:text-blue-500 underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="/app/iseewords/terms-of-service/" className="text-blue-600 hover:text-blue-500 underline" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IseeWordsRegistration;
